import React, { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { LocalShipping, Article, DirectionsBoat, Payments } from "@mui/icons-material";
import AppStoreLink from "../components/common/AppStoreLink";
import ColorModeToggle from "../components/common/ColorModeToggle";
import IosAppAnnouncementDialog from "../components/common/IosAppAnnouncementDialog";
import useIosAppAnnouncement from "../hooks/useIosAppAnnouncement";
import systemSettingsService from "../services/systemSettingsService";
import { injectSnippet } from "../utils/injectSnippet";

const AuthLayout = () => {
  // No auto-show here — the announcement is a post-login thing (see
  // CustomerLayout); on Login/Register it's only ever manually opened by
  // clicking the App Store link itself.
  const { open, openDialog, closeDialog } = useIosAppAnnouncement({ autoShowOnMount: false });

  // Admin-configured tracking (Google Tag Manager, etc. — see
  // AdminIntegrations.jsx) — injected ONLY here, on the public auth pages
  // (Login/Register/Forgot-Password/etc, all of AuthLayout's children),
  // never on an authenticated admin/customer page. Runs once per AuthLayout
  // mount (not per child page), so switching between Login and Register
  // doesn't inject GTM twice.
  useEffect(() => {
    let cleanupHead = () => {};
    let cleanupBody = () => {};
    systemSettingsService
      .getIntegrationSnippets()
      .then(({ data }) => {
        const integrations = data.data?.integrations || {};
        cleanupHead = injectSnippet(integrations.gtmHeadSnippet, document.head);
        cleanupBody = injectSnippet(integrations.gtmBodySnippet, document.body);
      })
      .catch(() => {
        /* Tracking is best-effort — never block/break the auth pages over it. */
      });
    return () => {
      cleanupHead();
      cleanupBody();
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background:
          "linear-gradient(135deg, #001A3C 0%, #002a5c 50%, #0D629B 100%)",
      }}
    >
      {/* Left branding panel */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          px: 8,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background pattern */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.04,
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />

        <Box sx={{ position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 6 }}>
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: 100,
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(8px)",
              }}
            >
              <img
                src="/logos/waypoint-logo.png"
                alt="Waypoint Imports"
                style={{ width: 60, height: 60, objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}
              >
                Waypoint Imports
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  opacity: 0.6,
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                }}
              >
                Cayman Islands
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: "2.25rem",
              lineHeight: 1.25,
            }}
          >
            Simplifying imports
            <br />
            to the Cayman Islands
          </Typography>
          <Typography
            sx={{ opacity: 0.7, fontSize: 15, lineHeight: 1.8, maxWidth: 440 }}
          >
            Track your shipments, manage customs declarations, upload documents,
            and stay informed — all in one place.
          </Typography>

          <Box
            sx={{ mt: 6, display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            {[
              {
                icon: <LocalShipping sx={{ fontSize: 18 }} />,
                text: "Real-time shipment tracking across 10 stages",
              },
              { icon: <Article sx={{ fontSize: 18 }} />, text: "Simplified customs declaration management" },
              {
                icon: <DirectionsBoat sx={{ fontSize: 18 }} />,
                text: "Sailing schedule updates and notifications",
              },
              { icon: <Payments sx={{ fontSize: 18 }} />, text: "Online invoice payments and receipts" },
            ].map((item) => (
              <Box
                key={item.text}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: 13.5, opacity: 0.85 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 5 }}>
            <AppStoreLink variant="light" onClick={openDialog} />
          </Box>
        </Box>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          width: { xs: "100%", lg: 480 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 3, sm: 5 },
          py: 5,
          bgcolor: "background.default",
          minHeight: "100vh",
          position: "relative",
        }}
      >
        <Box sx={{ position: "absolute", top: 16, right: 16 }}>
          <ColorModeToggle />
        </Box>

        {/* Mobile logo */}
        <Box
          sx={{
            display: { xs: "flex", lg: "none" },
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
            mb: 5,
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: 100,
              border: "1px solid #0D629B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/logos/waypoint-logo.png"
              alt="Waypoint Imports"
              style={{ width: 60, height: 60, objectFit: "contain" }}
            />
          </Box>

          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}
            >
              Waypoint Imports
            </Typography>

            <Typography
              sx={{
                fontSize: 10,
                color: "text.disabled",
                letterSpacing: "0.8px",
                textTransform: "uppercase",
              }}
            >
              Cayman Islands
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Outlet />
        </Box>

        {/* Shown here (not just the left branding panel) so it's visible
            on mobile too, where the left panel is hidden entirely. */}
        <Box sx={{ display: { xs: "flex", lg: "none" }, mt: 4 }}>
          <AppStoreLink variant="dark" onClick={openDialog} />
        </Box>
      </Box>

      <IosAppAnnouncementDialog open={open} onClose={closeDialog} />
    </Box>
  );
};

export default AuthLayout;
